/**
 * Dashboard Redirect Logic
 *
 * Pure function that determines which dashboard a user should be redirected to
 * based on their permissions. This logic is extracted from the router's beforeEnter
 * guard to make it easily testable without mocking Vue Router or Vuex.
 *
 * Priority order: QA Dashboard > Account Manager Dashboard > UAT Dashboard
 */

/**
 * Determines the dashboard route for a user based on their permissions
 *
 * @param {Object} permissions - User's dashboard permissions
 * @param {boolean} permissions.hasDashboardQaView - Can view QA dashboard
 * @param {boolean} permissions.hasDashboardAccountManagerView - Can view Account Manager dashboard
 * @param {boolean} permissions.hasDashboardUatView - Can view UAT dashboard
 * @param {string} permissions.serviceArea - User's service area (required for UAT dashboard)
 * @param {Object} currentRoute - Current route information
 * @param {string} currentRoute.name - Current route name
 * @param {string} currentRoute.packId - Current pack ID
 * @returns {Object|null} Route object to redirect to, or null if no redirect needed
 */
export function getDashboardRedirect(permissions, currentRoute) {
  const { hasDashboardQaView, hasDashboardAccountManagerView, hasDashboardUatView, serviceArea } = permissions;
  const { name: currentRouteName, packId } = currentRoute;

  // QA Dashboard has highest priority
  if (hasDashboardQaView) {
    // Don't redirect if already on QA dashboard
    if (currentRouteName === "qaDashboard") {
      return null;
    }
    return { name: "qaDashboard", params: { packId } };
  }

  // Account Manager Dashboard has second priority
  if (hasDashboardAccountManagerView) {
    // Don't redirect if already on Account Manager dashboard
    if (currentRouteName === "accountManagerDashboard") {
      return null;
    }
    return { name: "accountManagerDashboard", params: { packId } };
  }

  // UAT Dashboard has lowest priority
  if (hasDashboardUatView) {
    // Don't redirect if already on UAT dashboard
    if (currentRouteName === "uatDashboard") {
      return null;
    }
    return { name: "uatDashboard", params: { packId, serviceArea } };
  }

  // No dashboard permissions or no redirect needed
  return null;
}
